import BlockApi from '@/api/api';
import SearchField from '@/components/common/SearchField';
import FolderTreeView from '@/components/navigation/folder/FolderTreeView';
import { useConfig } from '@/hooks/useConfig';
import { BackendApplication, BackendApplicationRevision } from '@/types/backend';
import { Folder } from '@/types/folder';
import { applicationCustomTooltip, applicationSearchOptions, sortBy } from '@/util/application';
import { filterApplicationsInFolders, mergeFoldersAndApplications } from '@/util/folder';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  Typography
} from '@mui/material';
import { useEffect, useState, type FC } from 'react';
import { useAuth } from 'react-oidc-context';
import ApplicationRevisions from './ApplicationRevisions';

type ApplicationPickerDialogProps = {
  open: boolean;
  title: string;
  selectedAppId: string | undefined;
  onClose: () => void;
  onSelect: (application: BackendApplication, revision: number) => void;
};

const ApplicationPickerDialog: FC<ApplicationPickerDialogProps> = ({
  open,
  title,
  selectedAppId,
  onClose,
  onSelect
}) => {
  const { config } = useConfig();
  const auth = useAuth();

  const [selectedApplication, setSelectedApplication] = useState<BackendApplication>();
  const [selectedRevision, setSelectedRevision] = useState<BackendApplicationRevision>();
  const [applications, setApplications] = useState<BackendApplication[]>([]);
  const [mergedApplications, setMergedApplications] = useState<Folder[]>([]);
  const [filteredApps, setFilteredApps] = useState<Folder[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortAppsBy, setSortAppsBy] = useState('label');
  const [invert, setInvert] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  const getData = async () => {
    if (!auth?.user || selectedApplication) return;

    setLoading(true);

    const folders = await BlockApi(config.apiUrl, auth.user.access_token).folder.list();
    const apps = await BlockApi(config.apiUrl, auth.user.access_token).application.list();

    if (folders && apps) {
      setApplications(apps);
      setMergedApplications(mergeFoldersAndApplications(sortBy('name', folders), sortBy('label', apps)));
      const initialApp = apps.find((app) => app.applicationId === selectedAppId);
      initialApp && setSelectedApplication(initialApp);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (!open) return;

    !selectedAppId && setSelectedApplication(undefined);
    if (!applications.length) {
      getData();
    } else {
      const initialApp = applications.find((app) => app.applicationId === selectedAppId);
      initialApp && setSelectedApplication(initialApp);
    }
  }, [open]);

  useEffect(() => {
    if (!mergedApplications) return;
    setFilteredApps(filterApplicationsInFolders(mergedApplications, searchQuery, sortAppsBy, invert));
  }, [mergedApplications, searchQuery, sortAppsBy, invert]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack height={500} flexDirection='row' gap={1}>
          <Box flex={1}>
            <Box mb={1}>
              <SearchField
                sortBy={sortAppsBy}
                invert={invert}
                searchOptions={applicationSearchOptions}
                onSort={(changeSortBy, reverse) => {
                  changeSortBy && setSortAppsBy(changeSortBy);
                  reverse && setInvert(!invert);
                }}
                searchQuery={searchQuery}
                setSearchQuery={(query) => setSearchQuery(query)}
                customTooltip={applicationCustomTooltip}
                autoFocus={open}
              />
            </Box>
            {filteredApps.length > 0 ? (
              <FolderTreeView
                selectedItemId={selectedApplication?.applicationId}
                folders={filteredApps}
                onApplicationClick={(application) => setSelectedApplication(application)}
                maxHeight={450}
                disableFolderSelection
              />
            ) : loading ? (
              <LinearProgress />
            ) : (
              <Typography height={'100%'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
                No folders found
              </Typography>
            )}
          </Box>
          <Divider orientation='vertical' flexItem />
          <Box flex={1}>
            {selectedApplication && (
              <ApplicationRevisions
                application={selectedApplication}
                selectedRevision={selectedRevision}
                setSelectedRevision={(newRevision) => setSelectedRevision(newRevision)}
                confirmRevision={(newRevision) => onSelect(selectedApplication, newRevision)}
              />
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color='primary'>
          Cancel
        </Button>
        <Button
          variant='contained'
          disabled={!selectedApplication || !selectedRevision}
          onClick={() =>
            selectedApplication && selectedRevision && onSelect(selectedApplication, selectedRevision.revision)
          }
          color='primary'
        >
          Select
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApplicationPickerDialog;
